/**
 * The Trips screen: the trips the user has, and the door to planning a new one.
 *
 * This screen used to read **places** and never render a trip. It answered one question — is there
 * anything to plan with? — and its "Chưa có chuyến đi nào" was shown to every user who could plan,
 * including those with several trips stored on the server. The list of trips existed in the API
 * (`listTrips`) and in no screen, so a plan could only be seen again in the session that generated it.
 *
 * It now does both jobs, in that order: a saved trip is what the user came for, and the planability gate
 * is a note under it for when there is nothing, or when a new trip cannot be planned yet. The two facts
 * are kept apart rather than merged — "no trips yet" and "you cannot plan" have different fixes, and
 * showing one as the other is what made this screen's empty state misleading.
 *
 * **What this version adds, and why each thing is here rather than being decoration.** The list was one
 * full-width row per trip: a title on the left, two buttons against the right edge of a 1047px column, and
 * the eye travelling the whole way between them. It is a reading column now, and each trip is a card whose
 * parts are the facts a traveller actually holds in their head — when it is, how long, how many places,
 * who is going — with the two ways into it on the card itself.
 *
 * - The **date block** is the card's picture. Nothing in the contract carries a photograph of Đà Lạt, and
 *   the design system has no placeholders for one: a stock image of somebody else's holiday is a claim
 *   about this trip that the app cannot make. A date is the one image a trip always has.
 * - The **status** is the server's own `TripStatus`, all four values of it. `draft` and `planned` are the
 *   two a user can be in mid-flow; `completed` and `archived` exist in the contract, and a switch that
 *   quietly folded them into a fallback would be inventing state rather than reading it.
 * - The **counts** — nights and places — come from the server. The contract is explicit that nights are
 *   enumerated server-side because two places computing that arithmetic can disagree about how many nights
 *   a trip has; the list carries the same number for the same reason.
 * - **Managing a trip moved into a menu**, with the destructively-labelled item last and set apart. Two
 *   buttons at the row's far edge put "Xoá" one slip away from "Sửa" on a touch screen, and the answer to
 *   that is not a smaller button but a menu a user has to open on purpose.
 *
 * **What the restyle adds, and where each value comes from.** The card's anatomy is the design system's own:
 * a date tile, a title, a status pill and three glyph-led facts on the left, square icon actions on the
 * right, and a heading with the trip count beside it. Every size, colour, radius and timing on it is a
 * `--tp-*` token, and every word on it is in `copy.ts`, so the screen writes no literal — which is what makes
 * the page translatable and the palette the only place a visual value is changed. The glyphs are Material
 * Symbols ligature names inside `aria-hidden` spans: an icon identifier rather than a word, and the label
 * beside it is what a reader hears.
 */

import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { Link, useNavigate } from "react-router-dom";

import { copy } from "../copy.ts";
import { messageFor } from "../lib/errors.ts";
import { todayIso, tripDateRange } from "../lib/tripDates.ts";
import {
  useDeleteTrip,
  useDuplicateTrip,
  usePlaces,
  useRenameTrip,
  useTrips,
} from "../lib/queries.ts";
import { useCurrentUser } from "../lib/session-context.tsx";

/** Two located stops are the smallest set a route can be drawn through; one stop has no route. */
const MIN_ROUTABLE_PLACES = 2;

/**
 * The three ways of looking at a list of trips.
 *
 * By *when* the trip is, not by what has been done to it: the contract's `completed` is a status the server
 * sets and means the trip is finished with, which is a different fact from its dates having passed. A tab
 * named after the status over a date comparison would put two meanings on one word.
 */
type TripFilter = "all" | "upcoming" | "past";

const FILTERS: ReadonlyArray<{ key: TripFilter; label: (count: number) => string }> = [
  { key: "all", label: copy.trips.filterAll },
  { key: "upcoming", label: copy.trips.filterUpcoming },
  { key: "past", label: copy.trips.filterPast },
];

/**
 * How many days a date range covers, inclusive.
 *
 * The client counts **days** and not nights, deliberately: nights come from the server (see `nights_count`),
 * because the count depends on `stay_last_night` and the contract keeps that arithmetic in one place. A date
 * range is unambiguous, so counting it here cannot disagree with anything.
 */
function daysBetween(start: string, end: string): number | null {
  const from = Date.parse(`${start}T00:00:00Z`);
  const to = Date.parse(`${end}T00:00:00Z`);
  if (Number.isNaN(from) || Number.isNaN(to) || to < from) return null;
  return Math.round((to - from) / 86_400_000) + 1;
}

/**
 * The two short parts of a date, for the card's block: "03" over "TH10".
 *
 * The month's abbreviation is `copy.trips.monthShort` rather than a template here: "TH" is a word the reader
 * reads, and a word assembled in a screen is a word no translation can reach.
 */
function dateBlock(iso: string): { day: string; month: string } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (match === null) return null;
  return { day: match[3] as string, month: copy.trips.monthShort(Number(match[2])) };
}

/**
 * The label for a trip's status, one per value the contract defines.
 *
 * The tint is chosen at the element rather than here, in a literal the stylesheet scan can read: it mines
 * every string inside a `className={...}` expression, and a class assembled in a function is a class it
 * cannot see — which is the same reason `AppShell` reads its layout into a variable instead of comparing a
 * string in place.
 */
function statusLabel(status: string): string {
  switch (status) {
    case "planned":
      return copy.trips.statusPlanned;
    case "completed":
      return copy.trips.statusCompleted;
    case "archived":
      return copy.trips.statusArchived;
    default:
      return copy.trips.statusDraft;
  }
}

/**
 * The glyph that leads a status pill, one per value the contract defines.
 *
 * A Material Symbols ligature name, not a word: it is rendered inside an `aria-hidden` span, so it is read by
 * nobody and it is not copy. It is spelled out per status rather than derived, because the four states are
 * four different facts and a glyph chosen by a substring of a name is a glyph that changes when a name does.
 */
function statusGlyph(status: string): string {
  switch (status) {
    case "planned":
      return "route";
    case "completed":
      return "check_circle";
    case "archived":
      return "inventory_2";
    default:
      return "pending";
  }
}

export function TripsScreen(): ReactElement {
  const user = useCurrentUser();
  const userId = user?.id ?? "";
  const navigate = useNavigate();
  // The account is the planning scope here, not a grouping. The planner without a `?areaId=` plans
  // from everything saved, and picking a scope for the user on this screen was the bug it was: the old
  // fallback selected a grouping with zero places, so the planner opened holding nothing — which a user
  // cannot tell apart from the button being broken. This screen only asks whether planning is possible
  // at all.
  const placesQuery = usePlaces(userId);
  const tripsQuery = useTrips(userId);

  const places = placesQuery.data?.items ?? [];
  const locatedCount = places.filter((place) => place.location !== null).length;
  const canPlan = locatedCount >= MIN_ROUTABLE_PLACES;

  const trips = tripsQuery.data?.items ?? [];
  const deleteTrip = useDeleteTrip(userId);
  const renameTrip = useRenameTrip(userId);
  const duplicateTrip = useDuplicateTrip(userId);

  const [filter, setFilter] = useState<TripFilter>("all");
  /** Which card's menu is open, by id: one at a time, and closing is what opening another does. */
  const [menuFor, setMenuFor] = useState<string | null>(null);
  /** The trigger of the open menu, so Escape can hand focus back to the control it came from. */
  const menuTriggerRef = useRef<HTMLButtonElement | null>(null);
  /** The trip a rename is being typed for. */
  const [renameTarget, setRenameTarget] = useState<{ id: string; title: string } | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  /**
   * The trip a delete is being confirmed for.
   *
   * Held here and not on the row, because the row unmounts when the list refreshes and the dialog would go
   * with it — the same reason the places list holds its target at the screen.
   */
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);

  /*
   * Escape closes the open card menu and hands focus back to the trigger it came from.
   *
   * The menu is the sibling of the stretched card link, so a keyboard user reaches it by Tab and needs a
   * way out that is not "press the same control again" — closing onto the document body would send the next
   * Tab back to the top of the page. The listener is attached only while a menu is open, so no other key on
   * the screen pays for it.
   */
  useEffect(() => {
    if (menuFor === null) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      setMenuFor(null);
      menuTriggerRef.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuFor]);

  const today = todayIso();
  const upcoming = trips.filter((trip) => trip.end_date >= today);
  const past = trips.filter((trip) => trip.end_date < today);
  const visible = filter === "upcoming" ? upcoming : filter === "past" ? past : trips;
  const countFor = (key: TripFilter): number =>
    key === "upcoming" ? upcoming.length : key === "past" ? past.length : trips.length;

  return (
    <div className="screen trips-page">
      <div className="screen__head trips-page__head">
        <div className="trips-page__head-text">
          <div className="trips-page__head-title">
            <h1 className="screen__heading trips-page__heading">{copy.trips.heading}</h1>
            {/*
              The count, and only when there is one to count.

              "0 chuyến" beside the heading over the invitation below it is a number that contradicts the
              block it sits above, so the pill appears with the first trip and not before.
            */}
            {tripsQuery.isSuccess && trips.length > 0 && (
              <span className="trips-page__count">{copy.trips.tripCount(trips.length)}</span>
            )}
          </div>
          <p className="section__note">{copy.trips.purpose}</p>
        </div>
        {canPlan && (
          <button
            type="button"
            className="button button--primary trips-page__new"
            onClick={() => navigate("/trips/new")}
          >
            <span className="material-symbols-outlined trips-page__new-icon" aria-hidden="true">
              add
            </span>
            {copy.trips.newTrip}
          </button>
        )}
      </div>

      {/*
        The filters, and they appear only when there is something to filter.

        One trip is not a list to search, and three tabs over a single card is three controls that cannot
        change what is on screen. The counts are on the tabs because a filter that hides everything is a
        filter a user has to open to find that out.
      */}
      {tripsQuery.isSuccess && trips.length > 1 && (
        <div className="trips-page__filters" role="tablist" aria-label={copy.trips.heading}>
          {FILTERS.map((entry) => {
            const active = entry.key === filter;
            return (
              <button
                key={entry.key}
                type="button"
                role="tab"
                aria-selected={active}
                className={active ? "trips-page__filter trips-page__filter--on" : "trips-page__filter"}
                onClick={() => setFilter(entry.key)}
              >
                {entry.label(countFor(entry.key))}
              </button>
            );
          })}
        </div>
      )}

      {tripsQuery.isPending && <p className="state">{copy.state.loading}</p>}

      {tripsQuery.isError && (
        <div className="state">
          <p className="state__desc">{messageFor(tripsQuery.error)}</p>
          <button type="button" className="button" onClick={() => void tripsQuery.refetch()}>
            {copy.state.retry}
          </button>
        </div>
      )}

      {tripsQuery.isSuccess && trips.length > 0 && (
        <section className="trips-page__list-section">
          {visible.length === 0 ? (
            /*
             * A filter that matched nothing is not an empty account.
             *
             * Saying "chưa có kế hoạch nào" over a list the user is holding would be a lie about their own
             * data, which is the same class of mistake this screen was fixed for once already.
             */
            <p className="section__note">{copy.trips.filterEmpty}</p>
          ) : (
            <ul className="trips-page__list">
              {visible.map((trip) => {
                const block = dateBlock(trip.start_date);
                const range = tripDateRange(trip.start_date, trip.end_date);
                const days = daysBetween(trip.start_date, trip.end_date);
                /*
                 * Read into booleans before the markup, for the reason `AppShell` does the same thing: the
                 * stylesheet scan mines every quoted string inside a `className={...}` expression, so a status
                 * compared in place reads to it as three class names nobody styles.
                 */
                const planned = trip.status === "planned";
                const finished = trip.status === "completed" || trip.status === "archived";
                return (
                  <li
                    key={trip.id}
                    className={
                      menuFor === trip.id
                        ? "trips-page__card trips-page__card--menu-open"
                        : "trips-page__card"
                    }
                  >
                    {/*
                      The whole card is the way in, and the actions sit above it.

                      The link is stretched over the card rather than wrapping its text, which is what makes the
                      empty space between the facts and the buttons open the trip too — an area of a card that
                      looks like the card and does nothing is the small lie this fixes. The buttons are given a
                      z-index so they stay clickable on top of it.
                    */}
                    <Link className="trips-page__card__open" to={`/trips/${trip.id}`}>
                      <span className="visually-hidden">{`${copy.trips.openTrip}: ${trip.title}`}</span>
                    </Link>

                    {/*
                      The card's own head: the date on the left, the status and the overflow control on the
                      right. The date is the card's picture and the badge is what the trip is doing; both are
                      the first two things read, so both sit on the first line.
                    */}
                    <div className="trips-page__card__top">
                      <span className="trips-page__card__when" aria-hidden="true">
                        {block === null ? (
                          <span className="trips-page__card__day">{copy.trips.noDate}</span>
                        ) : (
                          <>
                            <span className="trips-page__card__day">{block.day}</span>
                            <span className="trips-page__card__month">{block.month}</span>
                          </>
                        )}
                      </span>
                      <div className="trips-page__card__top-end">
                        <span
                          className={
                            planned
                              ? "trips-page__card__status trips-page__card__status--planned"
                              : finished
                                ? "trips-page__card__status trips-page__card__status--past"
                                : "trips-page__card__status trips-page__card__status--draft"
                          }
                        >
                          {/*
                            A glyph before the label, hidden from assistive technology: the label already says
                            the state, so the glyph is only what lets a column of cards be scanned by shape.
                          */}
                          <span
                            className="material-symbols-outlined trips-page__card__status-glyph"
                            aria-hidden="true"
                          >
                            {statusGlyph(trip.status)}
                          </span>
                          {statusLabel(trip.status)}
                        </span>
                        <div className="trips-page__card__menu">
                          {/*
                            The overflow control keeps its own focus and its own Escape, which is why it is a
                            real sibling of the stretched link rather than a button inside it: a `<button>` in
                            an `<a>` is invalid markup, and the browser would make the menu's taps the link's.
                          */}
                          <button
                            type="button"
                            ref={menuFor === trip.id ? menuTriggerRef : null}
                            className="trips-page__card__menu-open"
                            aria-label={`${copy.trips.moreActions}: ${trip.title}`}
                            aria-expanded={menuFor === trip.id}
                            onClick={() => setMenuFor(menuFor === trip.id ? null : trip.id)}
                          >
                            <span
                              className="material-symbols-outlined trips-page__card__action-glyph"
                              aria-hidden="true"
                            >
                              more_vert
                            </span>
                          </button>
                          {menuFor === trip.id && (
                            <div className="trips-page__card__menu-list" role="menu">
                              <button
                                type="button"
                                role="menuitem"
                                className="trips-page__card__menu-item"
                                onClick={() => {
                                  setMenuFor(null);
                                  setRenameDraft(trip.title);
                                  setRenameTarget({ id: trip.id, title: trip.title });
                                }}
                              >
                                {copy.trips.renameAction}
                              </button>
                              <button
                                type="button"
                                role="menuitem"
                                className="trips-page__card__menu-item"
                                disabled={duplicateTrip.isPending}
                                onClick={() => {
                                  setMenuFor(null);
                                  duplicateTrip.mutate({
                                    id: trip.id,
                                    title: `${trip.title} (${copy.trips.duplicateSuffix})`,
                                  });
                                }}
                              >
                                {copy.trips.duplicateAction}
                              </button>
                              {/*
                                Last, and set apart. It is the only item here that cannot be undone, and being
                                last is what keeps it away from the pointer on the way to the item above it.
                              */}
                              <button
                                type="button"
                                role="menuitem"
                                className="trips-page__card__menu-item trips-page__card__menu-item--danger"
                                onClick={() => {
                                  setMenuFor(null);
                                  setDeleteTarget({ id: trip.id, title: trip.title });
                                }}
                              >
                                {copy.trips.remove}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <h2 className="trips-page__card__title">{trip.title}</h2>

                    {/*
                      The facts, each led by the glyph that names it.

                      Three pairs rather than a run-on line: what a traveller holds in their head is when the
                      trip is and how long, how many places, and who is going — and the glyph is what lets a
                      card be read for one of them without reading all three.
                    */}
                    <p className="trips-page__card__facts">
                      {(range !== null || days !== null) && (
                        <span className="trips-page__card__fact">
                          <span
                            className="material-symbols-outlined trips-page__card__fact-glyph"
                            aria-hidden="true"
                          >
                            calendar_month
                          </span>
                          {range !== null && (
                            <span className="trips-page__card__fact-text">{range}</span>
                          )}
                          {days !== null && (
                            <span className="trips-page__card__fact-text">
                              {copy.trips.daysNights(days, trip.nights_count)}
                            </span>
                          )}
                        </span>
                      )}
                      <span className="trips-page__card__fact">
                        <span
                          className="material-symbols-outlined trips-page__card__fact-glyph"
                          aria-hidden="true"
                        >
                          location_on
                        </span>
                        {copy.trips.placeCount(trip.place_count)}
                      </span>
                      <span className="trips-page__card__fact">
                        <span
                          className="material-symbols-outlined trips-page__card__fact-glyph"
                          aria-hidden="true"
                        >
                          group
                        </span>
                        {copy.trips.partySize(trip.adults, trip.children)}
                      </span>
                    </p>

                    {/*
                      The card's foot: where the trip is going, and the one way in.

                      The arrow is its own link, not a button inside the stretched one, and it is pushed to the
                      card's end so the whole foot reads as the card's exit. Its accessible name is the action
                      ("Mở chuyến đi" / "Tiếp tục chỉnh sửa"), and the glyph itself is hidden from assistive
                      technology. A draft has not been planned, so its arrow opens the planner on it; a planned
                      trip has a route to read — "Xem lộ trình" over a draft would open the planner and read as
                      a broken itinerary.
                    */}
                    <div className="trips-page__card__footer">
                      <span className="trips-page__card__destination">{trip.destination}</span>
                      <Link
                        className="trips-page__card__go"
                        to={planned ? `/trips/${trip.id}` : `/trips/${trip.id}/edit`}
                        aria-label={planned ? copy.trips.openTrip : copy.trips.continueEditing}
                      >
                        <span
                          className="material-symbols-outlined trips-page__card__action-glyph"
                          aria-hidden="true"
                        >
                          arrow_forward
                        </span>
                      </Link>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {duplicateTrip.isError && (
        <p className="state state--error" role="alert">
          {messageFor(duplicateTrip.error)}
        </p>
      )}

      {/*
        Nothing saved yet, as the card that has not been made.

        The dashed create card is the spec's optional tile, and it is the **empty state** rather than a
        permanent tile in a populated grid: once there is a trip, the header above already carries the same
        action, and two doors to one action in one view is one door too many. It sits in the same grid as a
        real card, one column wide, so it reads as the missing card rather than as a second screen.
      */}
      {tripsQuery.isSuccess && trips.length === 0 && (
        <section className="trips-page__list-section">
          <div className="trips-page__list">
            <div className="trips-page__create">
              <span className="material-symbols-outlined trips-page__create-icon" aria-hidden="true">
                add
              </span>
              <h2 className="trips-page__empty-title">{copy.trips.emptyTitle}</h2>
              <p className="trips-page__empty-body">{copy.trips.emptyBody}</p>
              {canPlan ? (
                <button
                  type="button"
                  className="button button--primary"
                  onClick={() => navigate("/trips/new")}
                >
                  {copy.trips.newTrip}
                </button>
              ) : (
                <p className="section__note">{copy.trips.needsPlaces}</p>
              )}
            </div>
          </div>
        </section>
      )}

      {/*
        The planability gate, kept but no longer pretending to be the whole screen.

        Only shown when a new trip could not be planned *and* there is nothing saved to plan with, so it
        reads as a caveat rather than as the state of the account.
      */}
      {placesQuery.isSuccess && !canPlan && trips.length > 0 && (
        <div className="state">
          <p className="state__desc">
            {places.length === 0 ? copy.trips.needsPlaces : copy.trips.needsLocatedPlaces}
          </p>
          <Link className="button button--primary" to={places.length === 0 ? "/capture" : "/places"}>
            {places.length === 0 ? copy.trips.savePlaces : copy.capture.editHeading}
          </Link>
        </div>
      )}

      {/*
        Renaming, one field at a time.
      */}
      {renameTarget !== null && (
        <div
          className="places-page__modal-scrim"
          onClick={() => setRenameTarget(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="rename-trip-title"
        >
          <div className="places-page__modal" onClick={(event) => event.stopPropagation()}>
            <h2 id="rename-trip-title" className="places-page__modal-title">
              {copy.trips.renameHeading}
            </h2>
            <label className="field">
              <span className="field__label">{copy.trips.renameLabel}</span>
              <input
                className="field__input"
                value={renameDraft}
                autoFocus
                onChange={(event) => setRenameDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && renameDraft.trim() !== "") {
                    renameTrip.mutate(
                      { id: renameTarget.id, title: renameDraft.trim() },
                      { onSuccess: () => setRenameTarget(null) },
                    );
                  }
                }}
              />
            </label>
            {renameTrip.isError && (
              <p className="places-page__modal-desc" role="alert">
                {messageFor(renameTrip.error)}
              </p>
            )}
            <div className="places-page__modal-actions">
              <button type="button" className="button button--ghost" onClick={() => setRenameTarget(null)}>
                {copy.capture.cancel}
              </button>
              <button
                type="button"
                className="button button--primary"
                disabled={renameTrip.isPending || renameDraft.trim() === ""}
                onClick={() =>
                  renameTrip.mutate(
                    { id: renameTarget.id, title: renameDraft.trim() },
                    { onSuccess: () => setRenameTarget(null) },
                  )
                }
              >
                {renameTrip.isPending ? copy.state.loading : copy.trips.renameSave}
              </button>
            </div>
          </div>
        </div>
      )}

      {/*
        The confirm, and it names what actually goes.

        A trip owns no places — it is a view over them — so deleting one leaves every saved place where it is.
        Saying that is the difference between a dialog a user can answer and one they have to guess at, and
        guessing wrong in the cautious direction means never deleting a trip they wanted gone.
      */}
      {deleteTarget !== null && (
        <div
          className="places-page__modal-scrim"
          onClick={() => setDeleteTarget(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-delete-trip-title"
        >
          <div className="places-page__modal" onClick={(event) => event.stopPropagation()}>
            <h2 id="confirm-delete-trip-title" className="places-page__modal-title">
              {copy.trips.confirmRemove(deleteTarget.title)}
            </h2>
            <p className="places-page__modal-desc">{copy.trips.removeWarns}</p>
            {deleteTrip.isError && (
              <p className="places-page__modal-desc" role="alert">
                {messageFor(deleteTrip.error)}
              </p>
            )}
            <div className="places-page__modal-actions">
              <button type="button" className="button button--ghost" onClick={() => setDeleteTarget(null)}>
                {copy.capture.cancel}
              </button>
              <button
                type="button"
                className="button button--danger"
                disabled={deleteTrip.isPending}
                onClick={() => {
                  const target = deleteTarget;
                  deleteTrip.mutate(target.id, { onSuccess: () => setDeleteTarget(null) });
                }}
              >
                {deleteTrip.isPending ? copy.state.loading : copy.trips.remove}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
